import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';
import _ from 'lodash';
import { map as mapUtils } from '@gisatcz/ptr-utils';
import { mapConstants } from '@gisatcz/ptr-core';
import { grid } from '@gisatcz/ptr-tile-grid';
/* === HELPERS ======================================================================= */

/**
 * Get background layer in 'layer' format
 */

var getBackgroundLayerAsLayer = createCachedSelector([function (backgroundLayer) {
  return backgroundLayer;
}], function (backgroundLayer) {
  if (backgroundLayer) {
    return _objectSpread(_objectSpread({}, backgroundLayer), {}, {
      key: 'pantherBackgroundLayer'
    });
  } else {
    return null;
  }
})(function (backgroundLayer) {
  return JSON.stringify(backgroundLayer);
});
/**
 * Merge background layer definition with layers to one collection
 */

var mergeBackgroundLayerWithLayers = createCachedSelector([function (backgroundLayer) {
  return getBackgroundLayerAsLayer(backgroundLayer);
}, function (backgroundLayer, layers) {
  return layers;
}], function (backgroundLayer, layers) {
  var finalLayers = layers || [];

  if (backgroundLayer) {
    finalLayers = [backgroundLayer].concat(_toConsumableArray(finalLayers));
  }

  return finalLayers.length ? finalLayers : null;
})(function (backgroundLayer, layers) {
  return "".concat(JSON.stringify(backgroundLayer), "_").concat(JSON.stringify(layers));
});
/**
 * Merge given modifiers with layer's modifiers & given filterByActive with layer's filter by active and add it to the layer state
 *
 * @param layers {Object} layers state
 * @param metadataModifiers {Object} modifiers like scopeKey, placeKey
 * @param filterByActive {{scope: bool, place: bool, period: bool,  scenario: bool, case: bool}}
 * @return {Object} Final layer state definition
 */

var mergeModifiersAndFilterByActiveToLayerStructure = function mergeModifiersAndFilterByActiveToLayerStructure(layers, metadataModifiers, filterByActive) {
  return layers.map(function (layer) {
    var layerMetadataModifiers = layer.metadataModifiers && metadataModifiers ? _objectSpread(_objectSpread({}, metadataModifiers), layer.metadataModifiers) : metadataModifiers || layer.metadataModifiers || null;
    var layerFilterByActive = layer.filterByActive && filterByActive ? _objectSpread(_objectSpread({}, filterByActive), layer.filterByActive) : filterByActive || layer.filterByActive || null;
    return _objectSpread(_objectSpread({}, layer), {}, {
      metadataModifiers: layerMetadataModifiers,
      filterByActive: layerFilterByActive
    });
  });
};
/**
 * It returns merged view from map and associated map set based on synced params
 * @param map {Object}
 * @param set {Object}
 * @return {Object|unknown} final map view
 */


var getView = function getView(map, set) {
  if (map) {
    if (set) {
      var _map$data, _set$data;

      var mapView = (_map$data = map.data) === null || _map$data === void 0 ? void 0 : _map$data.view; // omit synced view params from map

      if (set.sync && !_.isEmpty(set.sync)) {
        mapView = _.omitBy(mapView, function (viewValue, viewKey) {
          return set.sync[viewKey];
        });
      }

      var mapSetView = (_set$data = set.data) === null || _set$data === void 0 ? void 0 : _set$data.view;
      return mapUtils.view.mergeViews(mapConstants.defaultMapView, mapSetView, mapView);
    } else {
      var _map$data2;

      var view = (_map$data2 = map.data) === null || _map$data2 === void 0 ? void 0 : _map$data2.view;
      return mapUtils.view.mergeViews(mapConstants.defaultMapView, view);
    }
  } else {
    return null;
  }
};
/**
 * It returns merged previous view from map and associated map set based on synced params
 * @param map {Object}
 * @param set {Object}
 * @return {Object|unknown} final map view
 */


var getPreviousView = function getPreviousView(map, set) {
  if (map) {
    if (set) {
      var _map$data3, _set$data2;

      var mapPreviousView = (_map$data3 = map.data) === null || _map$data3 === void 0 ? void 0 : _map$data3.previousView; // omit synced view params from map

      if (set.sync && !_.isEmpty(set.sync)) {
        mapPreviousView = _.omitBy(mapPreviousView, function (viewValue, viewKey) {
          return set.sync[viewKey];
        });
      }

      var mapSetPreviousView = (_set$data2 = set.data) === null || _set$data2 === void 0 ? void 0 : _set$data2.previousView;
      return mapUtils.view.mergeViews(mapSetPreviousView, mapPreviousView);
    } else {
      var _map$data4;

      return (_map$data4 = map.data) === null || _map$data4 === void 0 ? void 0 : _map$data4.previousView;
    }
  } else {
    return null;
  }
};
/**
 * Return default style required by leaflet for rendering COGs
 */


var getDefaultCogStyle = createSelector([], function () {
  return {
    rules: [{
      styles: [{
        color: '#000000'
      }, {
        bandIndex: 0,
        values: {
          0: {
            color: null
          }
        }
      }]
    }]
  };
});
/**
 * Get zoom level of current view represented by mapWidth, mapHeight and boxRange.
 */

var getZoomLevel = createCachedSelector([function (mapWidth) {
  return mapWidth;
}, function (mapWidth, mapHeight) {
  return mapHeight;
}, function (mapWidth, mapHeight, boxRange) {
  return boxRange;
}], function (mapWidth, mapHeight, boxRange) {
  var viewportRange = mapUtils.view.getMapViewportRange(mapWidth, mapHeight);
  var levelBoxRange = mapUtils.view.getNearestZoomLevelBoxRange(mapWidth, mapHeight, boxRange);
  return grid.getLevelByViewport(levelBoxRange, viewportRange);
})(function (mapWidth, mapHeight, boxRange) {
  return "".concat(mapWidth).concat(mapHeight).concat(boxRange);
});
/**
 * Get tiles intersected by map extent.
 * Map extent is represented by mapWidth, mapHeight, center and boxRange.
 */

var getTiles = createCachedSelector([function (mapWidth) {
  return mapWidth;
}, function (mapWidth, mapHeight) {
  return mapHeight;
}, function (mapWidth, mapHeight, center) {
  return center;
}, function (mapWidth, mapHeight, center, boxRange) {
  return boxRange;
}], function (mapWidth, mapHeight, center, boxRange) {
  if (mapWidth && mapHeight && center && boxRange) {
    var levelBoxRange = mapUtils.view.getNearestZoomLevelBoxRange(mapWidth, mapHeight, boxRange);
    var tileGrid = grid.getTileGrid(mapWidth, mapHeight, levelBoxRange, center, true);
    return tileGrid.flat(1);
  } else {
    return null;
  }
})(function (mapWidth, mapHeight, center, boxRange) {
  return "".concat(mapWidth).concat(mapHeight).concat(center === null || center === void 0 ? void 0 : center.lon).concat(center === null || center === void 0 ? void 0 : center.lat).concat(boxRange);
});
export default {
  getBackgroundLayerAsLayer: getBackgroundLayerAsLayer,
  getTiles: getTiles,
  getPreviousView: getPreviousView,
  getView: getView,
  getZoomLevel: getZoomLevel,
  mergeBackgroundLayerWithLayers: mergeBackgroundLayerWithLayers,
  mergeModifiersAndFilterByActiveToLayerStructure: mergeModifiersAndFilterByActiveToLayerStructure,
  getDefaultCogStyle: getDefaultCogStyle
};